import type { RouteViewModel } from '../../../types/routeViewModel';
import type {
  EnrichedExecutionStop,
  ExecutionProgressHydration
} from '../../executionOperations/types';
import { cargoAtStop, isLoadingStop } from '../../executionOperations/executionHelpers';
import {
  enrichExecutionCargo,
  type EnrichedExecutionCargo,
  type HumanizedCargoMeta
} from './cargoHumanization';
import {
  getExecutionStatusLabel,
  normalizeOperationalState,
  type OperationalExecutionState
} from './statusFacade';

export type CargoLineSummary = {
  cargoId: number;
  name: string;
  quantity: number;
  weightKg: number | null;
  loadedQty: number;
  unloadedQty: number;
  remainingQty: number;
};

export type StopOperationalContext = {
  applicationNumbers: string[];
  applicationLabel: string | null;
  clientName: string | null;
  consigneeName: string | null;
  warehouseName: string | null;
  cargoLines: CargoLineSummary[];
  loadedLines: CargoLineSummary[];
  remainingLines: CargoLineSummary[];
  totalPlaces: number;
  totalWeightKg: number;
  plannedAt: string | null;
  actualAt: string | null;
};

export type ActiveExecutionStopView = {
  stop: EnrichedExecutionStop;
  operationalState: OperationalExecutionState;
  typeLabel: 'Погрузка' | 'Выгрузка';
  statusLabel: string;
  address: string | null;
  plannedAt: string | null;
  actualAt: string | null;
  progressPercent: number;
  cargoPlanned: number;
  cargoLoaded: number;
  cargoUnloaded: number;
  cargoRemaining: number;
  context: StopOperationalContext;
};

export function formatOperationalDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
}

function toStr(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

function toIso(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string' && value) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

export type StructureStopMeta = {
  address: string | null;
  contactPerson: string | null;
  phone: string | null;
  plannedAt: string | null;
  actualAt: string | null;
  city: string | null;
  applicationId: number | null;
  groupId: number | null;
  pointType: string | null;
  pointId: number | null;
  contractorName: string | null;
  warehouseName: string | null;
};

/** Index route structure stops by DB stop id. */
export function buildStructureStopMetaIndex(
  structureStops: Array<Record<string, unknown>>
): Map<number, StructureStopMeta> {
  const map = new Map<number, StructureStopMeta>();
  for (const s of structureStops) {
    const id = Number(s.id);
    if (!Number.isFinite(id)) continue;
    const contractor = s.contractor as Record<string, unknown> | undefined;
    const warehouse = s.warehouse as Record<string, unknown> | undefined;
    map.set(id, {
      address:
        toStr(s.address) ?? toStr(s.city) ?? null,
      contactPerson: toStr(s.contactPerson),
      phone: toStr(s.phone),
      plannedAt: toIso(s.plannedAt),
      actualAt: toIso(s.actualAt),
      city: toStr(s.city),
      applicationId: Number.isFinite(Number(s.applicationId)) ? Number(s.applicationId) : null,
      groupId: Number.isFinite(Number(s.groupId)) ? Number(s.groupId) : null,
      pointType: toStr(s.pointType),
      pointId: Number.isFinite(Number(s.pointId)) ? Number(s.pointId) : null,
      contractorName: toStr(contractor?.name),
      warehouseName: toStr(warehouse?.name)
    });
  }
  return map;
}

export function mergeStopOperationalMetadata(
  hydration: ExecutionProgressHydration,
  structureStops: Array<Record<string, unknown>>
): ExecutionProgressHydration {
  if (!structureStops.length) return hydration;
  const metaById = buildStructureStopMetaIndex(structureStops);

  return {
    ...hydration,
    stops: hydration.stops.map((stop) => {
      const meta = metaById.get(stop.stopId);
      if (!meta) return stop;
      return {
        ...stop,
        address: stop.address ?? meta.address,
        contactPerson: stop.contactPerson ?? meta.contactPerson,
        phone: stop.phone ?? meta.phone,
        plannedAt: stop.plannedAt ?? meta.plannedAt,
        actualAt: stop.actualAt ?? meta.actualAt,
        city: stop.city ?? meta.city,
        applicationId: stop.applicationId ?? meta.applicationId,
        groupId: stop.groupId ?? meta.groupId,
        pointType: stop.pointType ?? meta.pointType,
        pointId: stop.pointId ?? meta.pointId,
        contractorName: stop.contractorName ?? meta.contractorName,
        warehouseName: stop.warehouseName ?? meta.warehouseName
      };
    })
  };
}

function resolvePlannedAt(stop: EnrichedExecutionStop): string | null {
  return stop.plannedAt ?? null;
}

function resolveActualAt(stop: EnrichedExecutionStop): string | null {
  if (isLoadingStop(stop.type)) {
    return (
      stop.loadingCompletedAt ??
      stop.loadingStartedAt ??
      stop.actualAt ??
      stop.arrivedAt
    );
  }
  return (
    stop.unloadingCompletedAt ??
    stop.unloadingStartedAt ??
    stop.actualAt ??
    stop.arrivedAt
  );
}

function buildApplicationIndex(view: RouteViewModel): Map<number, Record<string, unknown>> {
  const map = new Map<number, Record<string, unknown>>();
  for (const entry of view.applications ?? []) {
    const app = (entry.application ?? entry) as Record<string, unknown>;
    const id = Number(app.id);
    if (Number.isFinite(id)) map.set(id, app);
  }
  return map;
}

function appNumber(app: Record<string, unknown> | undefined): string | null {
  if (!app) return null;
  return toStr(app.externalNumber) ?? toStr(app.internalNumber) ?? null;
}

function partyName(party: Record<string, unknown> | null | undefined): string | null {
  return toStr(party?.name);
}

function cargoToLine(c: EnrichedExecutionCargo, qtyField: 'loadedQty' | 'unloadedQty' | 'remainingQty'): CargoLineSummary {
  const qty =
    qtyField === 'loadedQty'
      ? c.loadedQty
      : qtyField === 'unloadedQty'
        ? c.unloadedQty
        : c.remainingQty;
  return {
    cargoId: c.cargoId,
    name: c.human.name,
    quantity: qty,
    weightKg: c.human.weightKg,
    loadedQty: c.loadedQty,
    unloadedQty: c.unloadedQty,
    remainingQty: c.remainingQty
  };
}

export function buildStopOperationalContext(params: {
  stop: EnrichedExecutionStop;
  hydration: ExecutionProgressHydration;
  cargoIndex: Map<number, HumanizedCargoMeta>;
  view: RouteViewModel;
}): StopOperationalContext {
  const { stop, hydration, cargoIndex, view } = params;
  const isLoading = isLoadingStop(stop.type);
  const rawCargo = cargoAtStop(hydration.cargo, stop.executionStopUid, stop.type);
  const enrichedCargo = enrichExecutionCargo(rawCargo, cargoIndex);
  const appIndex = buildApplicationIndex(view);

  const applicationIds = new Set<number>();
  if (stop.applicationId != null) applicationIds.add(stop.applicationId);
  for (const c of enrichedCargo) {
    const row = view.cargo.find((r) => Number(r.id) === c.cargoId);
    const aid = row ? Number((row as Record<string, unknown>).applicationId) : null;
    if (aid != null && Number.isFinite(aid)) applicationIds.add(aid);
  }

  const applicationNumbers: string[] = [];
  const appIdList = Array.from(applicationIds);
  for (const appId of appIdList) {
    const num = appNumber(appIndex.get(appId));
    if (num && !applicationNumbers.includes(num)) applicationNumbers.push(num);
  }

  const firstApp = appIdList.length > 0 ? appIndex.get(appIdList[0]) : undefined;
  const routeClient = partyName(view.parties?.client as Record<string, unknown> | undefined);
  const routeConsignee = partyName(view.parties?.consignee as Record<string, unknown> | undefined);
  const appConsignee = partyName(firstApp?.consignee as Record<string, unknown> | undefined);
  const appClient = partyName(
    (firstApp?.client as Record<string, unknown> | undefined) ??
      (firstApp?.shipper as Record<string, unknown> | undefined)
  );

  const cargoLines: CargoLineSummary[] = enrichedCargo.map((c) => ({
    cargoId: c.cargoId,
    name: c.human.name,
    quantity: c.plannedQty,
    weightKg: c.human.weightKg,
    loadedQty: c.loadedQty,
    unloadedQty: c.unloadedQty,
    remainingQty: c.remainingQty
  }));

  const loadedLines = enrichedCargo
    .filter((c) => c.loadedQty > 0)
    .map((c) => cargoToLine(c, 'loadedQty'));

  const remainingLines = enrichedCargo
    .filter((c) => {
      const rem = isLoading ? c.plannedQty - c.loadedQty : c.plannedQty - c.unloadedQty;
      return rem > 0;
    })
    .map((c) => {
      const rem = isLoading ? c.plannedQty - c.loadedQty : c.plannedQty - c.unloadedQty;
      return {
        ...cargoToLine(c, 'remainingQty'),
        quantity: rem,
        remainingQty: rem
      };
    });

  const totalPlaces = cargoLines.reduce((s, l) => s + l.quantity, 0);
  const totalWeightKg = cargoLines.reduce((s, l) => s + (l.weightKg ?? 0), 0);

  return {
    applicationNumbers,
    applicationLabel:
      applicationNumbers.length > 0
        ? applicationNumbers.map((n) => `Заявка №${n}`).join(', ')
        : null,
    clientName: appClient ?? routeClient,
    consigneeName: !isLoading
      ? appConsignee ?? routeConsignee ?? stop.contractorName ?? null
      : appClient ?? routeClient ?? null,
    warehouseName: stop.warehouseName ?? null,
    cargoLines,
    loadedLines,
    remainingLines,
    totalPlaces,
    totalWeightKg: totalWeightKg > 0 ? Math.round(totalWeightKg) : 0,
    plannedAt: resolvePlannedAt(stop),
    actualAt: resolveActualAt(stop)
  };
}

export function buildRouteCargoSummary(params: {
  hydration: ExecutionProgressHydration;
  cargoIndex: Map<number, HumanizedCargoMeta>;
}): {
  loadedLines: CargoLineSummary[];
  remainingLines: CargoLineSummary[];
  totalPlaces: number;
  totalWeightKg: number;
} {
  const enriched = enrichExecutionCargo(params.hydration.cargo, params.cargoIndex);
  const loadedLines = enriched
    .filter((c) => c.loadedQty > 0)
    .map((c) => cargoToLine(c, 'loadedQty'));
  const remainingLines = enriched
    .filter((c) => c.plannedQty - c.unloadedQty > 0)
    .map((c) => ({
      ...cargoToLine(c, 'remainingQty'),
      quantity: c.plannedQty - c.unloadedQty,
      remainingQty: c.plannedQty - c.unloadedQty
    }));
  return {
    loadedLines,
    remainingLines,
    totalPlaces: enriched.reduce((s, c) => s + c.plannedQty, 0),
    totalWeightKg: Math.round(enriched.reduce((s, c) => s + (c.human.weightKg ?? 0), 0))
  };
}

export function formatCargoLinesList(lines: CargoLineSummary[]): string[] {
  return lines.map((l) => `${l.name} — ${l.quantity} шт`);
}

export function getActiveExecutionStop(
  hydration: ExecutionProgressHydration,
  enrichedStops: EnrichedExecutionStop[],
  contextByStopUid: Map<string, StopOperationalContext>
): ActiveExecutionStopView | null {
  if (enrichedStops.length === 0) return null;

  const withState = enrichedStops.map((stop) => ({
    stop,
    operationalState: normalizeOperationalState(stop.executionState)
  }));

  const inProgress = withState.find((s) =>
    ['loading_in_progress', 'unloading_in_progress', 'partially_delivered'].includes(s.operationalState)
  );
  const pending = withState.find((s) =>
    ['loading_pending', 'unloading_pending', 'pending_transit'].includes(s.operationalState)
  );
  const chosen = inProgress ?? pending;
  const pick = chosen ?? withState.find((s) => !s.stop.isCompleted);
  if (!pick) return null;

  return buildActiveView(pick.stop, pick.operationalState, contextByStopUid);
}

function buildActiveView(
  stop: EnrichedExecutionStop,
  operationalState: OperationalExecutionState,
  contextByStopUid: Map<string, StopOperationalContext>
): ActiveExecutionStopView {
  const isLoading = isLoadingStop(stop.type);
  const cargoRemaining = isLoading
    ? Math.max(0, stop.cargoPlanned - stop.cargoLoaded)
    : Math.max(0, stop.cargoPlanned - stop.cargoUnloaded);
  const context =
    contextByStopUid.get(stop.executionStopUid) ??
    ({
      applicationNumbers: [],
      applicationLabel: null,
      clientName: null,
      consigneeName: null,
      warehouseName: null,
      cargoLines: [],
      loadedLines: [],
      remainingLines: [],
      totalPlaces: 0,
      totalWeightKg: 0,
      plannedAt: resolvePlannedAt(stop),
      actualAt: resolveActualAt(stop)
    } satisfies StopOperationalContext);

  return {
    stop,
    operationalState,
    typeLabel: isLoading ? 'Погрузка' : 'Выгрузка',
    statusLabel: getExecutionStatusLabel(operationalState),
    address: stop.address,
    plannedAt: context.plannedAt,
    actualAt: context.actualAt,
    progressPercent: stop.completionPercent,
    cargoPlanned: stop.cargoPlanned,
    cargoLoaded: stop.cargoLoaded,
    cargoUnloaded: stop.cargoUnloaded,
    cargoRemaining,
    context
  };
}

export function buildStopContextMap(params: {
  hydration: ExecutionProgressHydration;
  enrichedStops: EnrichedExecutionStop[];
  cargoIndex: Map<number, HumanizedCargoMeta>;
  view: RouteViewModel;
}): Map<string, StopOperationalContext> {
  const map = new Map<string, StopOperationalContext>();
  for (const stop of params.enrichedStops) {
    map.set(
      stop.executionStopUid,
      buildStopOperationalContext({
        stop,
        hydration: params.hydration,
        cargoIndex: params.cargoIndex,
        view: params.view
      })
    );
  }
  return map;
}
